/**
 * Format a numeric value as currency for display.
 *
 * @param {number|null|undefined} value - The amount to format.
 * @param {string} lang - 'fr' or 'en'.
 * @param {object} [opts]
 * @param {boolean} [opts.estimate] - When true, prefix with "~" and use muted styling hint.
 * @returns {string} Formatted currency string, e.g. "$1,229.92" or "~1 229,92 $"
 */
export function formatCurrency(value, lang, opts = {}) {
  if (value == null || isNaN(value)) return '—';

  const num = Number(value);
  const isFr = lang === 'fr';

  let formatted;
  if (isFr) {
    // fr-CA: space thousands, comma decimal, $ suffix → "1 229,92 $"
    formatted = num.toLocaleString('fr-CA', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }) + ' $';
  } else {
    // en-CA: comma thousands, period decimal, $ prefix → "$1,229.92"
    formatted = '$' + num.toLocaleString('en-CA', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  if (opts.estimate) {
    formatted = '~' + (isFr ? '' : '') + formatted;
  }

  return formatted;
}

/**
 * Format a percentage for display.
 * @param {number|null} value
 * @param {number} [decimals=1]
 * @returns {string}
 */
export function formatPercent(value, decimals = 1) {
  if (value == null || isNaN(value)) return '—';
  return Number(value).toFixed(decimals) + '%';
}

/**
 * Format a month string "YYYY-MM" into a localized label.
 * @param {string} yyyyMm - e.g. "2026-03"
 * @param {string} lang - 'fr' or 'en'
 * @returns {string} e.g. "Mar 2026" or "mars 2026"
 */
export function formatMonth(yyyyMm, lang) {
  if (!yyyyMm) return '';
  const [year, month] = yyyyMm.split('-');
  const date = new Date(Number(year), Number(month) - 1, 15);
  const locale = lang === 'fr' ? 'fr-CA' : 'en-CA';
  const monthName = date.toLocaleDateString(locale, { month: 'short' });
  return `${monthName} ${year}`;
}
